// ─── AutoChinBot · KN1 — Trend-candle conversation controller ──
// Turns task1's "trend candle" (big candle) detection into the interactive
// KN1 conversation on Telegram, and drives the "nhắc lại" (repeat) schedule.
//
// Design (agreed with the user):
//   • Triggered by task1 — every detected trend candle starts a KN1.
//   • The whole decision tree (Test branches, shared [Vào lệnh] entry) lives in
//     the `telegram-bot` edge function; this controller only:
//       1) sends the KN1 init message when a trend candle appears, and
//       2) ticks the edge function so due "nhắc lại" messages (every 1h) go out.
//   • IT RUNS IN THE BROWSER (same as task1–task6) — the app must be open for
//     reminders to fire, consistent with the rest of the pipeline.

import telegramBot from '@/lib/telegram-bot-client';
import type { AutoChinTaskEvent } from '@/lib/autochin-etask-engine';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';

/** How often we ask the edge function to flush due "nhắc lại" messages. */
const TICK_INTERVAL_MS = 60_000;

export class AutoChinKn1Controller {
  private started = false;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private logListeners = new Set<(l: AutoChinLogInput) => void>();

  // ─── Lifecycle ──────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    this.tickTimer = setInterval(() => { void this.tick(); }, TICK_INTERVAL_MS);
  }

  stop(): void {
    this.started = false;
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  // ─── task1 → KN1 init ───────────────────────────────────────

  /** task1 detected a trend candle → start the KN1 conversation. */
  async notifyBigCandle(e: AutoChinTaskEvent): Promise<void> {
    const direction: 'BULL' | 'BEAR' = e.direction === 'BEAR' ? 'BEAR' : 'BULL';
    try {
      const res = await telegramBot.sendKn1({
        symbol: e.symbolName,
        symbol_id: e.symbolId,
        direction,
        body_pips: e.bodyPips ?? null,
        threshold_pips: e.thresholdPips ?? null,
      });
      if (res.success && res.skipped) return; // an active KN1 already exists
      if (res.success) {
        this.emitLog({
          level: 'info',
          symbolId: e.symbolId,
          symbolName: e.symbolName,
          message:
            `KN1 · ${e.symbolName} — đã gửi thông báo khởi tạo nến xu hướng ` +
            `(${direction === 'BULL' ? 'Buy' : 'Sell'})`,
        });
      } else {
        this.emitLog({
          level: 'warn',
          symbolId: e.symbolId,
          symbolName: e.symbolName,
          message: `KN1 · ${e.symbolName} — ⚠️ không gửi được thông báo: ${res.error || 'unknown'}`,
        });
      }
    } catch (err) {
      this.emitLog({ level: 'error', message: `KN1 · lỗi gửi thông báo: ${String(err)}` });
    }
  }

  // ─── Repeat tick (nhắc lại mỗi 1H) ──────────────────────────

  private async tick(): Promise<void> {
    try {
      const res = await telegramBot.kn1Tick();
      if (res.success && res.resent && res.resent > 0) {
        this.emitLog({
          level: 'info',
          message: `KN1 · đã gửi lại ${res.resent} thông báo khởi tạo theo lịch nhắc (1H)`,
        });
      }
    } catch {
      /* best-effort — retry on the next tick */
    }
  }

  // ─── Emit ───────────────────────────────────────────────────

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }
}